import jwtDecode from 'jwt-decode';
// routes
import { updateError } from 'src/redux/slices/user';
//
import axios from './axios';
import { dispatch } from '../redux/store';
import { captureAuthSessionCleared } from './authSessionTelemetry';
import { decodeUserIdentity } from './authRefresh';
// Dependency-free (no firebase/apiCall) so importing it here can never create
// an import cycle back into this low-level, widely-imported module. See
// src/utils/fcmFingerprintStorage.js for why this isn't imported from
// src/fcmService.js instead.
import { clearFCMRegistrationFingerprint } from './fcmFingerprintStorage';
import { clearAllStaticLookupCache } from './staticLookupCache';
import { clearIdentityCache } from './leadIdentityCache';
// ----------------------------------------------------------------------

const isValidToken = (accessToken) => {
    if (!accessToken) {
        return false;
    }
    try {
        const decoded = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;
        return decoded.exp > currentTime;
    } catch {
        return false;
    }
};

let accessExpiredTimer;
let refreshExpiredTimer;

const handleTokenExpired = (exp, isRefresh) => {
    const currentTime = Date.now() / 1000;
    let timeLeft = (exp - currentTime) * 1000;
    if (isRefresh) timeLeft = Math.max(0, timeLeft - 60000);

    if (isRefresh) {
        clearTimeout(refreshExpiredTimer);
        refreshExpiredTimer = setTimeout(() => {
            dispatch(updateError({
                message: 'Please login again! Session will close in 1 minute',
                success: "warning"
            }));
        }, timeLeft);
    } else {
        // Access-token expiry is handled silently: the API interceptor catches
        // the 401 and runs the refresh flow. No user-visible toast — if refresh
        // itself fails, the refresh-expired branch above fires the "please
        // login again" warning.
        clearTimeout(accessExpiredTimer);
    }
};

const clearSessionTimers = () => {
    clearTimeout(accessExpiredTimer);
    clearTimeout(refreshExpiredTimer);
};

const clearStoredSession = (reason, telemetry = {}) => {
    captureAuthSessionCleared(reason, telemetry);
    // RCA guard: timers MUST be cleared here. Without this, leaked timers
    // from handleTokenExpired survive logout and fire updateError dispatches
    // into a logged-out app, which the Sentry Redux enhancer captures.
    clearSessionTimers();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common.Authorization;
};

const setSession = (accessToken, refreshToken, telemetry = {}) => {
    if (accessToken && refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        // This function below will handle when token is expired
        try {
            const { exp } = jwtDecode(accessToken); // ~3 days by minimals server
            const { exp: rexp } = jwtDecode(refreshToken);
            handleTokenExpired(exp, false);
            handleTokenExpired(rexp, true);
        } catch {
            clearStoredSession(telemetry.reason || 'auth_init_malformed_token', telemetry);
        }
    } else {
        clearStoredSession(telemetry.reason || 'session_cleared', telemetry);
        // Centralised here (rather than only in JWTContext's performLocalLogout)
        // so EVERY session-clear path — manual logout, peer-tab logout, an
        // unverifiable cross-tab identity change, and clearInvalidSession()'s
        // auth-refresh failures — always re-arms FCM registration. Without
        // this, an auto-logout followed by the same user re-logging into the
        // same practice with an unrotated Firebase token inside the
        // fingerprint TTL would silently skip re-POSTing the device.
        // NOTE: PR #4173 also adds a line here for a different cache — this
        // line is intentionally its own statement to keep that merge trivial.
        clearFCMRegistrationFingerprint();
        // Centralised invalidation point: every path that tears a session down
        // (manual logout, peer logout, refresh_invalid/expired via
        // clearInvalidSession in authRefresh.js, user-changed detection here
        // and in the storage-event handler, auth-init malformed/failed token
        // in JWTContext.jsx) ultimately calls setSession(null, null, ...).
        // Clearing the static-lookup TTL cache here means none of those call
        // sites need to remember to do it themselves.
        clearAllStaticLookupCache();
        // Create-lead identity-check results hold personal data: never survive a session.
        clearIdentityCache();
    }
};

// Peer-logout pub/sub. setSession(null, null, ...) above only clears
// tokens/axios-header/timers — the REST of a full logout (Redux practice
// selection, permissions, notifications, drafts, React auth state) lives in
// JWTContext.logout(). A peer tab's logout (or an unverifiable identity
// change landing on this tab) must run that SAME teardown, not a
// hand-rolled subset that can drift from it. JWTContext subscribes on mount
// and replays its own local-logout steps through this callback.
const peerLogoutListeners = new Set();

const onPeerLogout = (callback) => {
    peerLogoutListeners.add(callback);
    return () => peerLogoutListeners.delete(callback);
};

const emitPeerLogout = (reason) => {
    peerLogoutListeners.forEach((callback) => {
        try {
            callback(reason);
        } catch {
            // A subscriber's own error must never break sibling subscribers
            // or the storage-event handler itself.
        }
    });
};

function handleStorageEvent(event) {
    if (event.key === 'accessToken' && event.newValue) {
        // A tab holding NO prior token has no identity to defend, and a sibling
        // tab LOGGING IN writes accessToken with oldValue === null.
        // decodeUserIdentity(null) reports {ok: false}, which the sameUser test
        // below cannot distinguish from a genuinely foreign token — so a plain
        // login used to land in the teardown branch. That was self-inflicted and
        // cascading: the teardown calls setSession(null, null), which removes
        // BOTH tokens from the SHARED localStorage; the tab that just logged in
        // then receives refreshToken -> null and treats it as peer_logout. One
        // login with two tabs open logged both of them out, leaving the
        // dashboard stuck on 401s (/auth/user/get_context/).
        // Returning here is strictly safer than the old else-branch: it installs
        // no unverified bearer AND destroys no session. A foreign token landing
        // on a tab that has none is simply ignored.
        if (!event.oldValue) {
            return;
        }
        // A sibling tab wrote a new access token (refresh or adoption).
        // Verify it belongs to the SAME user as the token this tab last knew
        // about before installing it as our bearer — the write itself proves
        // nothing about identity, only that some tab produced it. Uses the
        // exact same identity extraction as resolvePeerRotation.
        const previous = decodeUserIdentity(event.oldValue);
        const incoming = decodeUserIdentity(event.newValue);
        const sameUser = previous.ok && incoming.ok && previous.userId && incoming.userId
            && previous.userId === incoming.userId;
        if (sameUser) {
            // Mirror it onto this tab's axios instance so subsequent
            // requests here carry the current bearer token, same as
            // setSession does.
            axios.defaults.headers.common.Authorization = `Bearer ${event.newValue}`;
        } else {
            // Different (or unverifiable) identity landed on this tab —
            // never install it as our bearer. Tear the session down instead;
            // no token values are logged.
            setSession(null, null, { reason: 'refresh_user_changed' });
            emitPeerLogout('refresh_user_changed');
        }
    } else if (event.key === 'refreshToken' && event.newValue === null) {
        // A sibling tab logged out (removed the refresh token). Mirror the
        // clear locally without a network call or hard navigation.
        setSession(null, null, { reason: 'peer_logout' });
        emitPeerLogout('peer_logout');
    }
}

// Cross-tab sync. The `storage` event fires only in OTHER documents/tabs —
// never in the tab that performed the write — so this can only ever react to
// a sibling tab's write, not our own; no self-triggering guard is needed.
//
// Guarded against double-registration (e.g. Vite HMR re-evaluating this
// module) with a flag on `window`: a second `addEventListener('storage', ...)`
// call would otherwise stack a second handler and double every downstream
// effect (teardown, Sentry events) per real event.
function attachStorageListener() {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return;
    }
    if (window.__crmAuthStorageListenerAttached) {
        return;
    }
    window.__crmAuthStorageListenerAttached = true;
    window.addEventListener('storage', handleStorageEvent);
}

attachStorageListener();

export {
    isValidToken, setSession, clearSessionTimers, onPeerLogout,
};

export const __jwtTestUtils = {
    attachStorageListener,
    handleStorageEvent,
};
